import React, { useCallback, useEffect, useState } from 'react'
import { Drawer, Space, Table, Tag, notification } from 'antd'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import _ from 'lodash'

import { generateMessageError } from 'modules/core/utils'
import configuration from 'modules/configuration'
import control from 'modules/control'

import {
  buildDimensionFilter,
  fetchCachedDimensionValues,
  generateDetailColumns,
  splitCellFilters,
} from './utils'

const DEFAULT_PAGE_SIZE = 20

const RealDetailDrawer = ({
  visible,
  onClose,
  cellFilters,
  conceptId,
  periodId,
  currencyId,
  allDimensionsList,
  fetchRealDetailRows,
  fetchAllDimensionsValues,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [detailRows, setDetailRows] = useState([])
  const [count, setCount] = useState(0)
  const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE })

  const { t } = useTranslation()

  const { months, dimensionFilters } = splitCellFilters(cellFilters)
  const cellKey = JSON.stringify(cellFilters)

  const page = pagination.cellKey === cellKey ? pagination.page : 1
  const { pageSize } = pagination

  const fetchDimensionValues = useCallback(
    (dimensionId) =>
      fetchCachedDimensionValues(dimensionId, (id) =>
        fetchAllDimensionsValues({ id_dimension: id, include_deleted: true }).then(
          (response) => response.payload.data,
        ),
      ),
    [fetchAllDimensionsValues],
  )

  useEffect(() => {
    setDetailRows([])
    setCount(0)
  }, [cellKey])

  useEffect(() => {
    if (!visible || _.isEmpty(cellFilters) || !currencyId) return

    let isCurrent = true
    setIsLoading(true)

    buildDimensionFilter({
      dimensionFilters,
      allDimensionsList,
      fetchDimensionValues,
    })
      .then(({ dimensionFilter, unresolvedColumns }) => {
        if (!isCurrent) return null

        if (!_.isEmpty(unresolvedColumns)) {
          notification.error({
            message: t('FEEDBACK_DEFAULT_ERROR'),
            description: t('CONTROL_DETAIL_ROWS_UNRESOLVED_FILTERS', {
              columns: unresolvedColumns.join(', '),
            }),
            duration: 0,
          })
          onClose()
          return null
        }

        return fetchRealDetailRows({
          concept_id: parseInt(conceptId),
          period_id: parseInt(periodId),
          currency_id: currencyId,
          months,
          dimension_filter: dimensionFilter,
          page,
          page_size: pageSize,
        })
      })
      .then((response) => {
        if (!isCurrent || !response) return
        setDetailRows(response.payload.data.result)
        setCount(response.payload.data.count)
      })
      .catch((error) => {
        if (!isCurrent) return
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false)
      })

    return () => {
      isCurrent = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, cellKey, currencyId, page, pageSize])

  const renderCellTags = () => (
    <Space wrap size={[4, 4]}>
      {Object.entries(dimensionFilters).map(([columnName, values]) => (
        <Tag key={columnName}>{`${columnName}: ${values.join(', ')}`}</Tag>
      ))}
      {months.map((month) => (
        <Tag key={month}>{month}</Tag>
      ))}
    </Space>
  )

  return (
    <Drawer
      title={
        <Space direction="vertical" size={4}>
          {t('CONTROL_DETAIL_ROWS_DRAWER_TITLE')}
          {renderCellTags()}
        </Space>
      }
      placement="right"
      width="80%"
      visible={visible}
      onClose={onClose}
      destroyOnClose
    >
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={detailRows}
        columns={generateDetailColumns(detailRows)}
        scroll={{ x: true }}
        pagination={{
          current: page,
          pageSize,
          total: count,
          showSizeChanger: true,
          size: 'small',
          onChange: (nextPage, nextPageSize) => {
            setPagination({ cellKey, page: nextPage, pageSize: nextPageSize })
          },
        }}
      />
    </Drawer>
  )
}

RealDetailDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  cellFilters: PropTypes.object,
  conceptId: PropTypes.any,
  periodId: PropTypes.any,
  currencyId: PropTypes.any,
  allDimensionsList: PropTypes.array,
  fetchRealDetailRows: PropTypes.func,
  fetchAllDimensionsValues: PropTypes.func,
}

const mapStateToProps = (state) => ({
  allDimensionsList: configuration.selectors.getAllDimensionsList(state),
})

const mapDispatchToProps = {
  fetchRealDetailRows: control.actions.fetchRealDetailRows,
  fetchAllDimensionsValues: configuration.actions.fetchAllDimensionsValues,
}

export default connect(mapStateToProps, mapDispatchToProps)(RealDetailDrawer)
